import { EWorldMapSetType } from '__generated__/globalTypes';
import { CtxMapV2 } from 'components/mapV2/contexts/CtxMapV2';
import { MapV2StoryListViewItem } from 'components/mapV2/elements/MapV2StoryListViewItem';
import { useCategoryTranslations } from 'components/mapV2/hooks/useCategoryTranslations';
import { MapV2ModalListHeader } from 'components/mapV2/MapV2Modal/elements/MapV2ModalListHeader';
import { ScrollVertical } from 'elements/functional/ScrollVertical';
import { DetailQuery_moment } from 'graphql/query/__generated__/DetailQuery';
import { EFonts } from 'libs/styles/styling';
import { normalizeStringForSearch } from 'libs/utils/text/normalizeStringForSearch';
import { isDefined } from 'libs/utils/typesafe/isDefined';
import { useContext } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    height: string;
    withoutHeader?: boolean;
    stories: DetailQuery_moment[];
}

export const MapV2ModalList = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxMapV2 = useContext(CtxMapV2);
    const ctxModal = useContext(CtxModal);
    const isWide = ctxMapV2.isWide;
    const categoryTranslations = useCategoryTranslations();

    const selectedCategories = ctxView.map.worldMapSets
        .filter((e) => e.type === EWorldMapSetType.CATEGORY)
        .map((e) => ({ title: e.title, worldMapSetId: e.worldMapSetId }))
        .concat(
            ctxView.map.showHighlightedCategory
                ? {
                      worldMapSetId: 'highlights',
                      title: Localization.MAP_V2_CATEGORY_HIGHLIGHTS
                  }
                : null
        )
        .filter(isDefined)
        .filter((e) => ctxView.visibleMapSetIds.includes(e.worldMapSetId));

    const selectedCategory = selectedCategories.length === 1 ? selectedCategories[0] : null;

    const normalizedSearchValue = normalizeStringForSearch(ctxMapV2.search.value);

    const filteredStories = normalizedSearchValue
        ? props.stories.filter((story) => {
              const normalizedTitle = normalizeStringForSearch(story.header?.title);
              const normalizedText = normalizeStringForSearch(story.header?.text);
              return (
                  normalizedTitle.includes(normalizedSearchValue) ||
                  normalizedText.includes(normalizedSearchValue)
              );
          })
        : props.stories;

    function getTitle() {
        if (ctxModal.detailListModal?.storyIds) {
            return Localization.MAP_V2_SELECTED_GROUP;
        }
        if (selectedCategory?.title) {
            return categoryTranslations.translateTitle(selectedCategory.title);
        }
        return Localization.MAP_V2_ALL_PLACES;
    }
    return (
        <S_MapV2ModalList className={props.className}>
            {!props.withoutHeader && <MapV2ModalListHeader />}
            <S_ScrollVertical
                light
                $height={props.height}
                stopPropagation
                $isWide={isWide}
                $withoutHeader={props.withoutHeader}
            >
                <S_Content $isWide={isWide}>
                    {!props.withoutHeader && (
                        <S_TitleRow>
                            <S_Title>{getTitle()}</S_Title>
                            {filteredStories.length !== ctxView.allDetails.length && (
                                <S_FilterCounter>
                                    {filteredStories.length}/{ctxView.allDetails.length}{' '}
                                    {Localization.MAP_V2_PLACES_POSTFIX}
                                </S_FilterCounter>
                            )}
                        </S_TitleRow>
                    )}
                    <S_Stories $isWide={isWide}>
                        {filteredStories.map((story) => (
                            <MapV2StoryListViewItem key={story.storyId} story={story} />
                        ))}
                    </S_Stories>
                </S_Content>
            </S_ScrollVertical>
        </S_MapV2ModalList>
    );
};

const S_MapV2ModalList = styled.div`
    /* background: #eff5f6; */
    position: relative;
    display: flex;
    flex-direction: column;
`;

const S_ScrollVertical = styled(ScrollVertical)<{
    $height: string;
    $isWide: boolean;
    $withoutHeader: boolean;
}>`
    ${(props) => {
        if (props.$withoutHeader) {
            return css`
                height: calc(${() => props.$height} - ${() => (props.$isWide ? '0px' : '10px')});
            `;
        } else {
            return css`
                /* 134px is height of the header for List View  */
                height: calc(${() => props.$height} - ${() => (props.$isWide ? '134px' : '10px')});
            `;
        }
    }}

    position: relative;
    background: #fff;
    border-radius: 10px;
    &::-webkit-scrollbar {
        width: 6px;
    }
`;

const S_Content = styled.div<{ $isWide: boolean }>`
    border-radius: 10px;
    padding: 15px;
    ${(props) =>
        !props.$isWide &&
        css`
            padding-bottom: 75px;
        `}
`;

const S_TitleRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 15px 0;
`;

const S_Title = styled.div`
    font-family: ${EFonts.InterBold};
    font-size: 20px;
    color: #2a2a2d;
`;

const S_FilterCounter = styled.div`
    font-family: ${EFonts.InterMedium};
    font-size: 14px;
    color: #84848c;
`;

const S_Stories = styled.div<{ $isWide: boolean }>`
    display: flex;
    flex-direction: column;
    padding-bottom: ${(props) => (props.$isWide ? '0' : '60px')};
    gap: 10px;
`;
